import React, { useState, useMemo, useCallback } from "react";
import { TierTWOsignUpBulk } from '../../api/TierTWOsignUp';
import { checkUserExists } from '../../api/isUserExist'; // Import the new API
import {
  commonErrorToastMessage,
  commonSuccessToastMessage,
} from "../../components/toastify/AllToastifyErrors"

const Tier2UserManagement = () => {
  const [users, setUsers] = useState([
    { firstName: "", lastName: "", email: "", password: "", emailExists: false, emailChecking: false },
    { firstName: "", lastName: "", email: "", password: "", emailExists: false, emailChecking: false },
  ]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [hidden, setHidden] = useState(false);
  const [fieldErrors, setFieldErrors] = useState([{}, {}]);
  const [duplicateEmails, setDuplicateEmails] = useState([]); // Track duplicate emails

  // Debounce timer ref
  const emailCheckTimers = React.useRef({});

  // Check for malicious content (URLs, script tags, HTML)
  const containsMaliciousContent = (value) => {
    if (!value) return false;
    
    const maliciousPatterns = [
      /<script[^>]*>.*?<\/script>/gi,
      /<iframe[^>]*>.*?<\/iframe>/gi,
      /<object[^>]*>.*?<\/object>/gi,
      /<embed[^>]*>/gi,
      /javascript:/gi,
      /on\w+\s*=/gi,
      /(https?:\/\/|www\.)/gi,
      /<[^>]+>/g,
    ];
    
    return maliciousPatterns.some(pattern => pattern.test(value));
  };

  // Check password strength
  const checkPasswordStrength = (rawPassword) => {
    const password = String(rawPassword || "");
    const specialCharRegex = /[!@#\$%\^&\*\(\)_\+\-=\[\]\{\};':"\\|,.<>\/\?`~]/;
    const strength = {
      hasMinLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecialChar: specialCharRegex.test(password),
    };
    
    const score = Object.values(strength).filter(Boolean).length;
    return {
      ...strength,
      score,
      isStrong: Boolean(strength.hasMinLength && score >= 4),
      message:
        score === 5 ? "Strong password"
        : score >= 4 ? "Good password"
        : score >= 3 ? "Fair password"
        : "Weak password"
    };
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Check if email exists in database
  const checkEmailExists = useCallback(async (index, email) => {
    if (!email || !validateEmail(email)) {
      return;
    }

    // Clear any existing timer for this index
    if (emailCheckTimers.current[index]) {
      clearTimeout(emailCheckTimers.current[index]);
    }

    // Set checking state
    setUsers((prevUsers) =>
      prevUsers.map((u, i) => (i === index ? { ...u, emailChecking: true } : u))
    );

    // Debounce the API call
    emailCheckTimers.current[index] = setTimeout(async () => {
      try {
        const response = await checkUserExists(email);
        const exists = response.data?.exists || false;

        setUsers((prevUsers) =>
          prevUsers.map((u, i) => 
            i === index ? { ...u, emailExists: exists, emailChecking: false } : u
          )
        );

        setFieldErrors((prevErrors) => {
          const nextErrors = [...prevErrors];
          const errorsForIndex = { ...(nextErrors[index] || {}) };

          if (exists) {
            errorsForIndex.email = "User with this email already exists";
          } else if (errorsForIndex.email === "User with this email already exists") {
            delete errorsForIndex.email;
          }

          nextErrors[index] = errorsForIndex;
          return nextErrors;
        });
      } catch (error) {
        console.error("Error checking email:", error);
        setUsers((prevUsers) =>
          prevUsers.map((u, i) => (i === index ? { ...u, emailChecking: false } : u))
        );
      }
    }, 500); // 500ms debounce
  }, []);

  const handleInputChange = (index, field, value) => {
    setUsers((prevUsers) =>
      prevUsers.map((u, i) => (i === index ? { ...u, [field]: value } : u))
    );

    setFieldErrors((prevErrors) => {
      const nextErrors = prevErrors.map((err) => ({ ...err }));
      const errorsForIndex = { ...(nextErrors[index] || {}) };

      if (field === "email") {
        // Clear existing email error temporarily
        delete errorsForIndex.email;
        
        if (value && containsMaliciousContent(value)) {
          errorsForIndex.email = "Links, HTML tags, and scripts are not allowed";
        } else if (value && !validateEmail(value)) {
          errorsForIndex.email = "Invalid email format";
        } else if (value && validateEmail(value)) {
          // Check if email exists in database
          checkEmailExists(index, value);
        }
      } else if (field === "password") {
        const strength = value ? checkPasswordStrength(value) : null;
        if (value && (!strength || !strength.isStrong)) {
          errorsForIndex.password =
            "Password must be at least 8 characters with uppercase, lowercase, number, and special character";
        } else {
          delete errorsForIndex.password;
        }
      } else {
        if (value && containsMaliciousContent(value)) {
          errorsForIndex[field] = "Links, HTML tags, and scripts are not allowed";
        } else {
          delete errorsForIndex[field];
        }
      }

      nextErrors[index] = errorsForIndex;
      return nextErrors;
    });
  };

  // Check if form is valid for submission
  const isFormValid = useMemo(() => {
    const filledUsers = users.filter(
      (u) => u.email || u.password || u.firstName || u.lastName
    );

    if (filledUsers.length === 0) return false;

    // Check if ANY user has an existing email
    const hasExistingEmail = users.some((u, i) => {
      const hasAnyData = u.email || u.password || u.firstName || u.lastName;
      return hasAnyData && u.emailExists;
    });

    if (hasExistingEmail) return false;

    // Check if any email is still being checked
    const isCheckingEmail = users.some((u, i) => {
      const hasAnyData = u.email || u.password || u.firstName || u.lastName;
      return hasAnyData && u.emailChecking;
    });

    if (isCheckingEmail) return false;

    for (let i = 0; i < users.length; i++) {
      const u = users[i];
      const hasAnyData = u.email || u.password || u.firstName || u.lastName;
      
      if (hasAnyData) {
        if (!u.firstName || !u.lastName || !u.email || !u.password) {
          return false;
        }

        if (
          containsMaliciousContent(u.firstName) ||
          containsMaliciousContent(u.lastName) ||
          containsMaliciousContent(u.email)
        ) {
          return false;
        }

        if (!validateEmail(u.email)) {
          return false;
        }

        const strength = checkPasswordStrength(u.password);
        if (!strength.isStrong) {
          return false;
        }

        if (fieldErrors[i] && Object.keys(fieldErrors[i]).length > 0) {
          return false;
        }
      }
    }

    return true;
  }, [users, fieldErrors]);

  const handleAddUsers = async () => {
    const filledUsers = users
      .map((u) => ({
        FirstName: u.firstName?.trim(),
        LastName: u.lastName?.trim(),
        Email: u.email?.trim(),
        Password: u.password
      }))
      .filter((u) => u.Email || u.Password || u.FirstName || u.LastName);

    if (filledUsers.length === 0) {
      setMessage({ text: "Please fill in at least one user's details", type: "error" });
      commonErrorToastMessage("Please fill in at least one user's details");
      return;
    }

    // Final check for existing emails
    const existingEmails = users
      .filter((u, i) => (u.email || u.password || u.firstName || u.lastName) && u.emailExists)
      .map(u => u.email);

    if (existingEmails.length > 0) {
      const errorMsg = `The following email(s) already exist: ${existingEmails.join(", ")}`;
      setMessage({ text: errorMsg, type: "error" });
      commonErrorToastMessage(errorMsg);
      return;
    }

    for (let u of filledUsers) {
      if (!u.FirstName || !u.LastName || !u.Email || !u.Password) {
        setMessage({ text: "Please complete all fields (first name, last name, email, password) for each user", type: "error" });
        commonErrorToastMessage("Please complete all fields for each user");
        return;
      }

      if (
        containsMaliciousContent(u.FirstName) ||
        containsMaliciousContent(u.LastName) ||
        containsMaliciousContent(u.Email)
      ) {
        setMessage({ text: "Links, HTML tags, and scripts are not allowed in any field", type: "error" });
        commonErrorToastMessage("Links, HTML tags, and scripts are not allowed");
        return;
      }

      if (!validateEmail(u.Email)) {
        setMessage({ text: `Invalid email format: ${u.Email}`, type: "error" });
        commonErrorToastMessage(`Invalid email format: ${u.Email}`);
        return;
      }

      const strength = checkPasswordStrength(u.Password);
      if (!strength.isStrong) {
        setMessage({ text: "Password must be at least 8 characters with uppercase, lowercase, number, and special character", type: "error" });
        commonErrorToastMessage("Password requirements not met");
        return;
      }
    }

    setLoading(true);
    setMessage({ text: "", type: "" });

    try {
      const userId =
        localStorage.getItem("Id") ||
        localStorage.getItem("Userid") ||
        localStorage.getItem("userId") ||
        localStorage.getItem("id") ||
        null;

      if (!userId) {
        setMessage({ text: "Parent userId not found in localStorage.", type: "error" });
        commonErrorToastMessage("Parent userId not found");
        setLoading(false);
        return;
      }

      const payload = {
        userId: Number(userId),
        users: filledUsers.map(u => ({
          FirstName: u.FirstName,
          LastName: u.LastName,
          Email: u.Email,
          Password: u.Password,
          Occupation: "other",
          Version: "GPT"
        }))
      };

      const resp = await TierTWOsignUpBulk(payload);

      if (resp && (resp.status === 200 || resp.status === 201 || resp.status === 207)) {
        const respData = resp.data || {};
        const backendAdded = typeof respData.AddedUsers === "number" ? respData.AddedUsers : 2;
        localStorage.setItem("AddedUsers", String(Math.min(2, backendAdded)));
        window.dispatchEvent(new Event("tier2-updated"));

        if (respData.errors && respData.errors.length > 0) {
          respData.errors.forEach(err => {
            const errorMsg = `${err.Email}: ${err.error}`;
            commonErrorToastMessage(errorMsg);
          });
          
          if (respData.successCount > 0) {
            commonSuccessToastMessage(`Successfully added ${respData.successCount} user(s).`);
          }
        } else {
          commonSuccessToastMessage(respData.message || `Processed ${respData.successCount || 0} users.`);
        }

        if (respData.successCount > 0) {
          setUsers([
            { firstName: "", lastName: "", email: "", password: "", emailExists: false, emailChecking: false },
            { firstName: "", lastName: "", email: "", password: "", emailExists: false, emailChecking: false },
          ]);
          setFieldErrors([{}, {}]);
          
          if (!respData.errors || respData.errors.length === 0) {
            setHidden(true);
          }
        }
      } else {
        const errMsg = (resp && resp.data && (resp.data.error || JSON.stringify(resp.data))) || "Failed to add users";
        setMessage({ text: errMsg, type: "error" });
        commonErrorToastMessage(errMsg);
      }
    } catch (error) {
      console.error("Error adding tier2 users:", error);
      
      if (error?.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        error.response.data.errors.forEach(err => {
          const errorMsg = `${err.Email}: ${err.error}`;
          commonErrorToastMessage(errorMsg);
        });
        
        const generalMsg = error.response.data.message || "Some users could not be added.";
        setMessage({ text: generalMsg, type: "error" });
      } else {
        const errMsg = error?.response?.data?.message || error?.message || "An error occurred. Please try again.";
        setMessage({ text: errMsg, type: "error" });
        commonErrorToastMessage(errMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  // Detect duplicate emails across both inputs and set per-field errors + toast (instead of modal)
  React.useEffect(() => {
    const emails = users.map((u) => (u.email || "").trim().toLowerCase());
    const counts = emails.reduce((acc, e) => {
      if (!e) return acc;
      acc[e] = (acc[e] || 0) + 1;
      return acc;
    }, {});

    const duplicates = Object.keys(counts).filter((k) => counts[k] > 1);
    setDuplicateEmails(duplicates);

    // show toast error when duplicate detected (instead of modal)
    if (duplicates.length > 0) {
      const msg = `Duplicate email entered: ${duplicates.join(", ")}. Please use unique email addresses.`;
      commonErrorToastMessage(msg);
    }

    setFieldErrors((prevErrors) => {
      const next = prevErrors.map((err) => ({ ...err }));
      users.forEach((u, i) => {
        const em = (u.email || "").trim().toLowerCase();
        if (em && duplicates.includes(em)) {
          next[i] = { ...(next[i] || {}), email: "Duplicate email - please use a unique email" };
        } else {
          if (next[i] && next[i].email === "Duplicate email - please use a unique email") {
            const copy = { ...next[i] };
            delete copy.email;
            next[i] = copy;
          }
        }
      });
      return next;
    });
  }, [users]);

  // Cleanup timers on unmount
  React.useEffect(() => {
    return () => {
      Object.values(emailCheckTimers.current).forEach(timer => clearTimeout(timer));
    };
  }, []);

  if (hidden) return null;

  return (
    <div className="mt-6 bg-white rounded-lg shadow-md p-6 relative">
      <div className="flex items-center mb-4">
        <i className="fa-solid fa-users text-[#474785] text-2xl mr-3"></i>
        <h2 className="text-xl font-semibold text-[#212529]">
          Tier 2 - Add Team Members
        </h2>
      </div>
      <p className="text-gray-600 text-sm mb-6">
        As a Tier 2 user, you can add up to 2 additional team members to your account.
        <span className="block mt-1 text-yellow-600 font-medium">Note: All email addresses must be new users.</span>
      </p>

      <div className="space-y-4">
        {users.map((user, index) => {
          const passwordStrength = user.password ? checkPasswordStrength(user.password) : null;
          return (
            <div key={`user-${index}`} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
               <h3 className="text-sm font-medium text-gray-700 mb-3">
                 User {index + 1}
               </h3>
               <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                 <div>
                   <label className="block text-xs font-medium text-gray-700 mb-1">First Name</label>
                   <input
                     type="text"
                     value={user.firstName}
                     onChange={(e) => handleInputChange(index, "firstName", e.target.value)}
                     placeholder="First name"
                     className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#474785] text-sm ${
                       fieldErrors[index]?.firstName ? "border-red-500" : "border-gray-300"
                     }`}
                   />
                   {fieldErrors[index]?.firstName && (
                     <p className="text-xs text-red-600 mt-1">{fieldErrors[index].firstName}</p>
                   )}
                 </div>
                 <div>
                   <label className="block text-xs font-medium text-gray-700 mb-1">Last Name</label>
                   <input
                     type="text"
                     value={user.lastName}
                     onChange={(e) => handleInputChange(index, "lastName", e.target.value)}
                     placeholder="Last name"
                     className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#474785] text-sm ${
                       fieldErrors[index]?.lastName ? "border-red-500" : "border-gray-300"
                     }`}
                   />
                   {fieldErrors[index]?.lastName && (
                     <p className="text-xs text-red-600 mt-1">{fieldErrors[index].lastName}</p>
                   )}
                 </div>
                 <div>
                   <label className="block text-xs font-medium text-gray-700 mb-1">Email Address</label>
                   <div className="relative">
                     <input
                       type="email"
                       value={user.email}
                       onChange={(e) => handleInputChange(index, "email", e.target.value)}
                       placeholder="user@example.com"
                       className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#474785] text-sm ${
                         fieldErrors[index]?.email ? "border-red-500" : 
                         user.emailExists ? "border-red-500" :
                         user.email && !user.emailChecking && !user.emailExists && validateEmail(user.email) ? "border-green-500" :
                         "border-gray-300"
                       }`}
                     />
                     {user.emailChecking && (
                       <div className="absolute right-3 top-2.5">
                         <i className="fa-solid fa-spinner fa-spin text-gray-400"></i>
                       </div>
                     )}
                     {!user.emailChecking && user.email && validateEmail(user.email) && !user.emailExists && (
                       <div className="absolute right-3 top-2.5">
                         <i className="fa-solid fa-check-circle text-green-500"></i>
                       </div>
                     )}
                     {!user.emailChecking && user.emailExists && (
                       <div className="absolute right-3 top-2.5">
                         <i className="fa-solid fa-times-circle text-red-500"></i>
                       </div>
                     )}
                   </div>
                   {user.emailChecking && (
                     <p className="text-xs text-gray-500 mt-1">Checking availability...</p>
                   )}
                   {!user.emailChecking && user.email && validateEmail(user.email) && !user.emailExists && !fieldErrors[index]?.email && (
                     <p className="text-xs text-green-600 mt-1">✓ Email is available</p>
                   )}
                   {fieldErrors[index]?.email && (
                     <p className="text-xs text-red-600 mt-1">{fieldErrors[index].email}</p>
                   )}
                 </div>
                 <div>
                   <label className="block text-xs font-medium text-gray-700 mb-1">Password</label>
                   <input
                     type="password"
                     value={user.password}
                     onChange={(e) => handleInputChange(index, "password", e.target.value)}
                     placeholder="Minimum 8 characters"
                     className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#474785] text-sm ${
                       fieldErrors[index]?.password ? "border-red-500" : "border-gray-300"
                     }`}
                   />
                   {user.password && passwordStrength && (
                    <div className="mt-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-xs font-medium ${
                            passwordStrength.score === 5 ? "text-green-600" : 
                            passwordStrength.score >= 4 ? "text-blue-600" :
                            passwordStrength.score >= 3 ? "text-yellow-600" : "text-red-600"
                          }`}>
                          {passwordStrength.message}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div
                          className={`h-1.5 rounded-full transition-all ${
                            passwordStrength.score === 5 ? "bg-green-500" :
                            passwordStrength.score >= 4 ? "bg-blue-500" :
                            passwordStrength.score >= 3 ? "bg-yellow-500" : "bg-red-500"
                          }`}
                          style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                        ></div>
                      </div>

                      <div className="mt-2 space-y-1">
                        <div className={`${passwordStrength.hasMinLength ? "text-green-600" : "text-gray-600"} text-xs`}>
                          At least 8 characters
                        </div>
                        <div className={`${passwordStrength.hasUpperCase ? "text-green-600" : "text-gray-600"} text-xs`}>
                          Uppercase letter (A-Z)
                        </div>
                        <div className={`${passwordStrength.hasLowerCase ? "text-green-600" : "text-gray-600"} text-xs`}>
                          Lowercase letter (a-z)
                        </div>
                        <div className={`${passwordStrength.hasNumber ? "text-green-600" : "text-gray-600"} text-xs`}>
                          Number (0-9)
                        </div>
                        <div className={`${passwordStrength.hasSpecialChar ? "text-green-600" : "text-gray-600"} text-xs`}>
                          Special character (!@#$%...)
                        </div>
                      </div>
                    </div>
                  )}
                  {fieldErrors[index]?.password && (
                    <p className="text-xs text-red-600 mt-1">{fieldErrors[index].password}</p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {message.text && (
        <div
          className={`mt-4 p-3 rounded-md text-sm ${
            message.type === "success"
              ? "bg-green-100 text-green-800 border border-green-300"
              : "bg-red-100 text-red-800 border border-red-300"
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="mt-6 flex justify-end">
        <button
          onClick={handleAddUsers}
          disabled={loading || !isFormValid}
          className={`px-6 py-2 rounded-md text-white font-medium text-sm transition-all ${
            loading || !isFormValid ? "bg-gray-400 cursor-not-allowed" : "bg-[#474785] hover:bg-[#3a3a6b]"
          }`}
        >
          {loading ? (
            <span className="flex items-center">
               <i className="fa-solid fa-spinner fa-spin mr-2"></i>
              Adding Users...
            </span>
           ) : (
            <span className="flex items-center">
              <i className="fa-solid fa-user-plus mr-2"></i>
              Add Users
            </span>
          )}
        </button>
      </div>

      {loading && (
        <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center rounded-lg z-40">
          <div className="bg-white px-5 py-3 rounded-md flex items-center gap-3 shadow">
            <i className="fa-solid fa-spinner fa-spin text-2xl text-[#474785]" />
            <span className="text-sm font-medium text-[#474785]">Adding users...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tier2UserManagement;